import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const KamuMalAlimReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [institutionInfo, setInstitutionInfo] = useState({
    institutionName: '',
    institutionType: 'School',
    receivedBy: '',
    receiptDate: new Date().toISOString().split('T')[0]
  });

  const [items, setItems] = useState([
    { materialName: '', unit: 'pcs', quantity: 1, unitPrice: 0, total: 0 }
  ]);

  const [notes, setNotes] = useState('');

  useEffect(() => {
    const grandTotal = items.reduce((sum, item) => sum + item.total, 0);
    onDataChange({
      ...institutionInfo,
      items,
      notes,
      grandTotal,
      date: new Date().toLocaleDateString()
    });
  }, [institutionInfo, items, notes, onDataChange]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = newItems[index].quantity * newItems[index].unitPrice;
    }
    setItems(newItems);
  };

  const grandTotal = items.reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField label="Institution Name" name="institutionName" value={institutionInfo.institutionName} onChange={(e) => setInstitutionInfo({ ...institutionInfo, institutionName: e.target.value })} placeholder="e.g., Central Hospital" required />
        <div>
          <label className="flex flex-col">
            <p className="text-[#111418] dark:text-gray-100 text-base font-medium leading-normal pb-2">Institution Type</p>
            <select className="form-select flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#111418] dark:text-gray-100 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 h-14 p-[15px]" value={institutionInfo.institutionType} onChange={(e) => setInstitutionInfo({ ...institutionInfo, institutionType: e.target.value })}>
              <option>School</option>
              <option>Hospital</option>
              <option>Government Office</option>
              <option>Other</option>
            </select>
          </label>
        </div>
        <InputField label="Received By" name="receivedBy" value={institutionInfo.receivedBy} onChange={(e) => setInstitutionInfo({ ...institutionInfo, receivedBy: e.target.value })} placeholder="Name of receiver" />
        <InputField label="Receipt Date" name="receiptDate" type="date" value={institutionInfo.receiptDate} onChange={(e) => setInstitutionInfo({ ...institutionInfo, receiptDate: e.target.value })} />
      </div>

      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Supplies / Stock Entry</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Material Name</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Unit</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Quantity</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Unit Price</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">Total</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2"><input className="form-input w-full bg-transparent border-0 focus:ring-0 p-2" type="text" value={item.materialName} onChange={(e) => handleItemChange(index, 'materialName', e.target.value)} placeholder="e.g., Medical supplies, Books" /></td>
                  <td className="p-2">
                    <select className="form-select w-full bg-transparent border-0 focus:ring-0 p-2" value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)}>
                      <option>pcs</option>
                      <option>boxes</option>
                      <option>kg</option>
                      <option>liters</option>
                      <option>sets</option>
                    </select>
                  </td>
                  <td className="p-2"><input className="form-input w-20 bg-transparent border-0 focus:ring-0 p-2 text-center" type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} /></td>
                  <td className="p-2"><input className="form-input w-28 bg-transparent border-0 focus:ring-0 p-2 text-right" type="number" min="0" step="0.01" value={item.unitPrice} onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} /></td>
                  <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">${item.total.toFixed(2)}</td>
                  <td className="p-2 text-center"><button onClick={() => setItems(items.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={items.length === 1}><span className="material-symbols-outlined text-xl">delete</span></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setItems([...items, { materialName: '', unit: 'pcs', quantity: 1, unitPrice: 0, total: 0 }])} variant="ghost" icon="add_circle" className="mt-4">{t('form.addRow')}</Button>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-sm space-y-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <div className="flex justify-between items-center pt-3"><span className="text-lg font-bold text-[#111418] dark:text-gray-100">{t('form.grandTotal')}</span><span className="text-lg font-bold text-primary">${grandTotal.toFixed(2)}</span></div>
        </div>
      </div>

      <TextAreaField label={t('form.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Delivery notes, condition of goods..." rows={3} />
    </div>
  );
};

export default KamuMalAlimReportForm;
